import { useCallback, useEffect, useState } from 'react';
import { presentUnexpectedFailure, isUnexpectedFailureHandled } from '../../shared/sentry/presentUnexpectedFailure';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { useNavigation } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import {
  disconnectMercadoPago,
  fetchMercadoPagoPosList,
  linkMercadoPagoPosForBranch,
  openMercadoPagoOAuthBrowser,
  type MercadoPagoPosListItem,
  type MercadoPagoPosListMeta,
  type MercadoPagoPosPendingItem,
} from '../../mercadopago/infra/mercadopagoConnectService';
import { handleMercadoPagoOAuthReturnUrl, consumeMercadoPagoOAuthReloadPending } from '../../mercadopago/infra/mercadopagoOAuthCallback';
import type { MercadoPagoOAuthReturnShellRoute } from '../../mercadopago/infra/mercadopagoConnectService';
import { getMpPosConfig, saveMpPosConfig, type MpPosConfigRow } from '../../mercadopago/infra/mpSettingsRepo';
import { getCachedMpIntegrationStatus } from '../application/storefrontStatus';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';

export function useMercadoPagoBranchSettingsScreen(options?: {
  oauthReturnShellRoute?: MercadoPagoOAuthReturnShellRoute;
}) {
  const navigation = useNavigation();
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branches = useSessionStore((state) => state.branches);
  const { canView } = useBranchPermission('BranchSettings');
  const canManageMp = useModulePermission('BranchSettings', 'manage_mp');
  const { entitlements: planEntitlements } = usePlanEntitlements();
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const mercadoPagoIntegrationsEnabled = isGlobalFeatureOn(globalFeatureFlags, 'mercadopago_integrations');
  const mpQrSettingsAllowed = planEntitlements?.mp_qr_pos === true;
  const isOwner = planEntitlements?.is_owner === true;
  const canEdit = isOwner || canManageMp;
  const currentBranch = branches.find((branch) => branch.id === currentBranchId) ?? null;

  const [loading, setLoading] = useState(true);

  const [mpPosConfig, setMpPosConfig] = useState<MpPosConfigRow | null>(null);
  const [mpOAuthConnected, setMpOAuthConnected] = useState(false);
  const [mpPosExternalIdInput, setMpPosExternalIdInput] = useState('');
  const [mpPosSaving, setMpPosSaving] = useState(false);

  const [mpOAuthOpening, setMpOAuthOpening] = useState(false);
  const [mpDisconnecting, setMpDisconnecting] = useState(false);
  const [mpDisconnectConfirmOpen, setMpDisconnectConfirmOpen] = useState(false);
  const [mpPosListError, setMpPosListError] = useState<string | null>(null);
  const [mpPosListLoading, setMpPosListLoading] = useState(false);
  const [mpPosPickerOpen, setMpPosPickerOpen] = useState(false);
  const [mpPosPickerItems, setMpPosPickerItems] = useState<MercadoPagoPosListItem[]>([]);
  const [mpPosPickerPendingItems, setMpPosPickerPendingItems] = useState<MercadoPagoPosPendingItem[]>([]);
  const [mpPosListMeta, setMpPosListMeta] = useState<MercadoPagoPosListMeta | null>(null);
  const [mpPosPickerSaving, setMpPosPickerSaving] = useState(false);
  const [linkingPosInternalId, setLinkingPosInternalId] = useState<string | null>(null);

  const applyPosListResult = useCallback(
    (result: Awaited<ReturnType<typeof fetchMercadoPagoPosList>>) => {
      setMpPosPickerItems(result.items);
      setMpPosPickerPendingItems(result.pending_items);
      setMpPosListMeta(result.meta ?? null);
    },
    []
  );

  const load = useCallback(async (opts?: { force?: boolean }) => {
    if (!currentBranchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [mpConfig, mpStatus] = await Promise.all([
        getMpPosConfig(currentBranchId).catch(() => null),
        getCachedMpIntegrationStatus(currentBranchId, { force: opts?.force }).catch(() => ({
          oauth_connected: false,
          pos_configured: false,
        })),
      ]);
      setMpOAuthConnected(mpStatus.oauth_connected);
      setMpPosConfig(mpConfig);
      if (mpConfig) {
        setMpPosExternalIdInput(mpConfig.external_pos_id ?? '');
      } else {
        setMpPosExternalIdInput('');
      }
    } catch (e) {
      console.warn('[MercadoPagoBranchSettings] load', e);
    } finally {
      setLoading(false);
    }
  }, [currentBranchId]);

  useEffect(() => {
    if (consumeMercadoPagoOAuthReloadPending()) {
      setMpOAuthConnected(true);
      setMpPosListError(null);
      void load({ force: true });
    }
  }, [load]);

  const handleUpgradePlan = useCallback(() => {
    navigateInShell(navigation as NavigationProp<ParamListBase>, 'Billing');
  }, [navigation]);

  const checkMpAvailable = () => {
    if (!mercadoPagoIntegrationsEnabled) {
      showInfo('No disponible', 'Mercado Pago está apagado por ahora.');
      return false;
    }
    if (!mpQrSettingsAllowed) {
      showInfo('Plan', 'Mercado Pago QR no está disponible en tu plan actual.');
      return false;
    }
    return true;
  };

  const handleSaveMpPosConfig = async () => {
    if (!currentBranchId) return;
    if (!checkMpAvailable()) return;
    try {
      setMpPosSaving(true);
      const saved = await saveMpPosConfig(currentBranchId, mpPosExternalIdInput);
      setMpPosConfig(saved);
      void logBusinessAuditEvent('branch.settings.mp_pos', {
        branchId: currentBranchId,
        mode: saved.mode,
      });
      showSuccess('Guardado', 'Caja QR actualizada.');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar la caja QR.' });
    } finally {
      setMpPosSaving(false);
    }
  };

  const handleConnectMercadoPago = async () => {
    if (!currentBranchId || !canEdit) return;
    if (!checkMpAvailable()) return;
    try {
      setMpOAuthOpening(true);
      const result = await openMercadoPagoOAuthBrowser(currentBranchId, {
        returnShellRoute: options?.oauthReturnShellRoute ?? 'BranchSettings',
      });
      if (result.type === 'redirect') {
        return;
      }
      if (result.type === 'success' && 'url' in result && result.url) {
        handleMercadoPagoOAuthReturnUrl(result.url);
        setMpOAuthConnected(true);
        setMpPosListError(null);
        await load({ force: true });
      }
    } catch (e: any) {
      if (!isUnexpectedFailureHandled(e)) {
        presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo conectar con Mercado Pago.' });
      }
    } finally {
      setMpOAuthOpening(false);
    }
  };

  const handleDisconnectMercadoPago = async () => {
    if (!currentBranchId || !canEdit) return;
    if (!mpOAuthConnected) return;
    try {
      setMpDisconnecting(true);
      await disconnectMercadoPago(currentBranchId);
      setMpOAuthConnected(false);
      setMpPosConfig(null);
      setMpPosExternalIdInput('');
      setMpPosPickerItems([]);
      setMpPosPickerPendingItems([]);
      setMpDisconnectConfirmOpen(false);
      void logBusinessAuditEvent('branch.settings.mp_disconnected', {
        branchId: currentBranchId,
      });
      showSuccess('Mercado Pago desvinculado', 'Los tokens y la caja QR se eliminaron de Vuelto.');
    } catch (e: any) {
      presentUnexpectedFailure({
        module: 'settings',
        op: 'error',
        error: e,
        title: 'Error',
        userMessage: 'No se pudo desvincular Mercado Pago. Intentá de nuevo.',
      });
    } finally {
      setMpDisconnecting(false);
    }
  };

  const handleRefreshMercadoPagoPosPicker = async () => {
    if (!currentBranchId || !canEdit || !mpOAuthConnected) return;
    setMpPosListError(null);
    try {
      setMpPosListLoading(true);
      const result = await fetchMercadoPagoPosList(currentBranchId);
      applyPosListResult(result);
    } catch (e: any) {
      const msg =
        e?.message ??
        'No se pudieron listar las cajas. Creá la tienda y la caja en Mercado Pago o pegá el ID.';
      setMpPosListError(msg);
      if (!isUnexpectedFailureHandled(e)) {
        showError('No se pudieron listar las cajas', msg);
      }
    } finally {
      setMpPosListLoading(false);
    }
  };

  const handleOpenMercadoPagoPosPicker = async () => {
    if (!currentBranchId || !canEdit) return;
    if (!mpOAuthConnected) {
      showInfo(
        'Conectá Mercado Pago primero',
        'Antes de elegir una caja, conectá la cuenta con el botón de arriba.'
      );
      return;
    }
    if (!checkMpAvailable()) return;
    setMpPosListError(null);
    try {
      setMpPosListLoading(true);
      const result = await fetchMercadoPagoPosList(currentBranchId);
      applyPosListResult(result);
      setMpPosPickerOpen(true);
    } catch (e: any) {
      const msg =
        e?.message ??
        'No se pudieron listar las cajas. Creá la tienda y la caja en Mercado Pago y pegá el ID abajo.';
      setMpPosListError(msg);
      if (!isUnexpectedFailureHandled(e)) {
        showError('No se pudieron listar las cajas', msg);
      }
    } finally {
      setMpPosListLoading(false);
    }
  };

  const dismissMpPosListError = useCallback(() => setMpPosListError(null), []);

  const handleLinkMercadoPagoPos = async (item: MercadoPagoPosPendingItem) => {
    if (!currentBranchId || !canEdit) return;
    try {
      setMpPosPickerSaving(true);
      setLinkingPosInternalId(item.pos_internal_id);
      const result = await linkMercadoPagoPosForBranch(currentBranchId, item.pos_internal_id);
      applyPosListResult(result);

      const linked = result.items.find((row) => row.pos_internal_id === item.pos_internal_id);
      const externalId = linked?.external_pos_id ?? result.linkedExternalPosId;

      if (externalId) {
        setMpPosExternalIdInput(externalId);
        setMpPosConfig(await saveMpPosConfig(currentBranchId, externalId));
      }

      setMpPosPickerOpen(false);
      showSuccess(
        'Caja vinculada',
        externalId
          ? 'Mercado Pago quedó listo para cobrar con QR.'
          : 'Actualizá la lista si no ves la caja asignada.'
      );
      await load();
    } catch (e: any) {
      if (!isUnexpectedFailureHandled(e)) {
        presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo vincular la caja con Vuelto.' });
      }
    } finally {
      setMpPosPickerSaving(false);
      setLinkingPosInternalId(null);
    }
  };

  const handleSelectMercadoPagoPos = async (item: MercadoPagoPosListItem) => {
    if (!currentBranchId || !canEdit) return;
    if (!checkMpAvailable()) return;
    try {
      setMpPosPickerSaving(true);
      const saved = await saveMpPosConfig(currentBranchId, item.external_pos_id);
      setMpPosConfig(saved);
      setMpPosExternalIdInput(saved.external_pos_id ?? '');
      setMpPosPickerOpen(false);
      void logBusinessAuditEvent('branch.settings.mp_pos_from_list', {
        branchId: currentBranchId,
        mode: saved.mode,
      });
      showSuccess('Guardado', 'Caja de Mercado Pago asignada a este local.');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'settings', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo guardar la caja.' });
    } finally {
      setMpPosPickerSaving(false);
    }
  };

  return {
    currentBranchId,
    canView,
    canEdit,
    loading,
    load,
    currentBranch,
    mpQrSettingsAllowed,
    mercadoPagoIntegrationsEnabled,
    mpPosConfig,
    mpOAuthConnected,
    mpPosExternalIdInput,
    setMpPosExternalIdInput,
    mpPosSaving,
    handleSaveMpPosConfig,
    handleUpgradePlan,
    mpOAuthOpening,
    handleConnectMercadoPago,
    mpDisconnecting,
    mpDisconnectConfirmOpen,
    setMpDisconnectConfirmOpen,
    handleDisconnectMercadoPago,
    mpPosListError,
    dismissMpPosListError,
    mpPosListLoading,
    mpPosPickerOpen,
    setMpPosPickerOpen,
    mpPosPickerItems,
    mpPosPickerPendingItems,
    mpPosListMeta,
    mpPosPickerSaving,
    linkingPosInternalId,
    handleOpenMercadoPagoPosPicker,
    handleRefreshMercadoPagoPosPicker,
    handleLinkMercadoPagoPos,
    handleSelectMercadoPagoPos,
  };
}

export type MercadoPagoBranchSettingsScreenViewModel = ReturnType<typeof useMercadoPagoBranchSettingsScreen>;
